import { ContentCopy, DeleteOutline, Lock } from "@mui/icons-material";
import {
    Button,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-premium";
import moment from "moment";
import { PublishedIcon } from "../../../images/svg/publishedIcon";
import { WIPIcon } from "../../../images/svg/WIPIcon";
import { entityOptions } from "../../viewQuotes/itemDetails/utils";

export const PricingListColumn = (arg: {
    onDelete?: (row: any) => void,
    view?: (row: any) => void,
    updatePublishStatus?: (row: any) => void,
    disableNavigation?: boolean,
    removeDelete?: boolean,
    publishable?: boolean,
    getParentPath?: (id: string) => string,
    handleDuplicate?: (row: any) => void,
    bomRes: any[]
}) => {

    const {
        onDelete = () => false,
        view = () => false,
        updatePublishStatus = () => false,
        disableNavigation = false,
        removeDelete = false,
        publishable = false,
        getParentPath = () => "",
        handleDuplicate = () => false,
        bomRes = []
    } = arg;

    let columns: GridColDef[] = [
        {
            field: "sequence_id",
            headerName: "Pricing ID",
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }

                if (disableNavigation) {
                    return (
                        <Typography variant="body2">{params.row?.sequence_id}</Typography>
                    );
                }
                return (
                    <Stack direction="row" spacing={1} sx={{
                        alignItems: "center"
                    }}>
                        <Typography
                            id="view-pricing-icon"
                            component={"a"}
                            href={getParentPath(params.row?._id)}
                            sx={{
                                color: "blue",
                                textDecoration: "underline",
                                cursor: "pointer",
                            }}
                            variant="body2"
                            onClick={(e) => {
                                view(params.row);
                                e.preventDefault();
                            }}
                        >
                            {params.row?.sequence_id}
                        </Typography>
                        {params.row?.user_access ? <Tooltip title={`This ${params.row.sequence_id} is private`}><Lock color='disabled' fontSize={"small"} /></Tooltip> : <></>}
                    </Stack>
                );
            },
        },
        {
            field: "duplicate",
            headerName: "Duplicate",
            resizable: true,
            width: 90,
            disableColumnMenu: true,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }
                return (
                    <Tooltip title="Make a Duplicate">
                        <IconButton
                            sx={{ margin: "auto" }}
                            onClick={() =>
                                handleDuplicate(params?.row)
                            }
                        >
                            <ContentCopy fontSize="small" />
                        </IconButton>
                    </Tooltip>
                );
            },
        },
        {
            field: "pricing_name",
            headerName: "Pricing Name",
            width: 300,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }
                return (
                    <Tooltip title={params.row?.pricing_name}>
                        <Typography
                            variant="body2"
                            sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {params.row?.pricing_name}
                        </Typography>
                    </Tooltip>
                );
            }
        },
        {
            field: "entity_type",
            headerName: "Entity Type",
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }
                const association = params.row?.associations?.at(-1);
                const entity_type = entityOptions.find(e => e.value === association?.object_type)?.label;
                return (
                    <Tooltip title={entity_type}>
                        <Typography
                            variant="body2"
                            sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {entity_type}
                        </Typography>
                    </Tooltip>
                );
            },
            valueGetter: (_: any, row: any) => {
                const association = row?.associations?.at(-1);
                const entity_type = entityOptions.find(e => e.value === association?.object_type)?.label;
                return entity_type;
            },
        },
        {
            field: "entity",
            headerName: "Entity",
            width: 170,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }
                const association = params.row?.associations?.at(-1);
                const object_detail = association?.object_details?.at(-1);
                const entity = `${object_detail?.sequence_id ?? "..."}`;
                return (
                    <Tooltip title={entity}>
                        <Typography
                            variant="body2"
                            sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {entity}
                        </Typography>
                    </Tooltip>
                );
            },
            valueGetter: (_: any, row: any) => {
                const association = row?.associations?.at(-1);
                const object_detail = association?.object_details?.at(-1);
                const entity = `${object_detail?.sequence_id ?? "..."}`;
                return entity;
            },
        },
        {
            field: "bom_id",
            headerName: "BOM ID",
            width: 170,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }

                const bom = bomRes?.find(b => b?._id === params?.row?.bom_cost?._id);
                const label = bom ? `${bom.sequence_id}-r${bom.version}` : '';
                return (
                    <Tooltip title={label}>
                        <Typography
                            variant="body2"
                            sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {label}
                        </Typography>
                    </Tooltip>
                );
            },
            valueGetter: (_: any, row: any) => {
                const bom = bomRes?.find(b => b?._id === row?.bom_cost?._id);
                const label = bom ? `${bom.sequence_id}-r${bom.version}` : '';
                return label;
            },
        },
        {
            field: "currency",
            headerName: "Currency",
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }

                const label = params?.row?.exchange_rates?.base_currency;
                return (
                    <Tooltip title={label}>
                        <Typography
                            variant="body2"
                            sx={{ width: "100%", overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                            {label}
                        </Typography>
                    </Tooltip>
                );
            },
            valueGetter: (_: any, row: any) => {
                const label = row?.exchange_rates?.base_currency;
                return label;
            },
        },
        { field: "remarks", headerName: "Remarks", width: 250 },
        {
            field: "released",
            headerName: "Status",
            width: 150,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    params?.row?.released ? (
                        <Button disableRipple disableFocusRipple sx={{ cursor: "default", '&:hover': { backgroundColor: 'transparent' } }}>
                            <Tooltip
                                title="Published"
                                children={<div><PublishedIcon /></div>}
                            />
                        </Button>
                    ) : (
                        <Button disableRipple disableFocusRipple id="publish-material">
                            <Tooltip
                                title={publishable ? "Work in progress, click to publish the item" : "Not Published"}
                                children={
                                    <div><WIPIcon onClick={() => publishable ? updatePublishStatus(params.row) : false} /></div>
                                }
                            />
                        </Button>
                    )
                );
            },
        },
        { field: 'serial_num', headerName: 'Custom Reference No.', width: 200 },
        { field: "created_user", headerName: "Created By", width: 150 },
        {
            field: "created_time",
            headerName: "Created Date",
            width: 250,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }
                return (
                    <Typography variant="body2">
                        {moment(params.row.created_time).format("lll")}
                    </Typography>
                );
            },
        },
        { field: "modified_user", headerName: "Last modified by", width: 150 },
        {
            field: "modified_time",
            headerName: "Last modified date",
            width: 250,
            renderCell: (params: GridRenderCellParams) => {
                if (params.rowNode.type === "group") {
                    return params.value;
                }
                return (
                    <Typography variant="body2">
                        {params.row.modified_time
                            ? moment(params.row.modified_time).format("lll")
                            : ""}
                    </Typography>
                );
            },
        },
        // { field: 'released_user', headerName: 'Published by', width: 150 },
        // {
        //     field: 'released_time', headerName: 'Published date', width: 250, renderCell: (params: GridRenderCellParams) => {
        //         if (params.rowNode.type === 'group') {
        //             return params.value;
        //         }
        //         return <Typography variant="body2">{params.row.released_time ? moment(params.row.released_time).format('lll') : ""}</Typography>
        //     }
        // },
        {
            field: "delete",
            headerName: "Delete",
            width: 100,
            renderCell: (params: GridRenderCellParams) => {
                return (
                    <Tooltip title={params?.row?.released ? `${params?.row?.sequence_id} is published` : `Delete the Pricing ${params?.row?.sequence_id}`}>
                        <IconButton disableRipple={params?.row?.released} onClick={() => params?.row?.released ? () => { } : onDelete(params.row)}>
                            <DeleteOutline color={params?.row?.released ? "disabled" : "error"} id="delete-icon" />
                        </IconButton>
                    </Tooltip>
                );
            },
            disableColumnMenu: true,
            sortable: false,
        }
    ];

    if (removeDelete) {
        columns.pop();
    }

    return columns
};
